"use client";

import Accordion from "@JohnLewisPartnership/jl-design-system/dist/components/accordion/Accordion";
import { TertiaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import Plus24PxOutlinedIcon from "@JohnLewisPartnership/jl-design-system/dist/elements/icons-jb/Plus24PxOutlined";
import Minus24PxOutlinedIcon from "@JohnLewisPartnership/jl-design-system/dist/elements/icons-jb/Minus24PxOutlined";
import { DescriptionIcons } from "./DescriptionIcons";
import { DescriptionLinks } from "./DescriptionLinks";
import { ProductType } from "../../../../types/types";
import styles from "./ProductDescriptionAccordion.module.scss";
import { useRef, useState } from "react";
import classNames from "classnames";
import { useFading } from "./useFading";
import { Icon, Link } from "../../../../types/prodcatBaseTypes";
import { UnderlinedButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";

export type Props = {
  productType: ProductType;
  displayCode: string;
  description: string;
  icons: Icon[];
  links: Link[];
  endorsement:
    | {
        title: string;
        description?: string | null | undefined;
      }
    | undefined;
  open?: boolean;
  hideDescriptionLinks?: boolean;
  showBeautySocietyLink?: boolean;
};

export const PRODUCT_DESCRIPTION_ACCORDION_INSTANCE_NAME =
  "product-description";

export function ProductDescriptionAccordionUI({
  productType,
  displayCode,
  description,
  icons,
  links,
  endorsement,
  open,
  hideDescriptionLinks,
  showBeautySocietyLink,
}: Props) {
  const descriptionRef = useRef<HTMLDivElement>(null);
  const [truncated, setTruncated] = useState(false);
  const [contentHeight, doFade] = useFading(descriptionRef);

  const descriptionContainerStyle = styles.descriptionContainer;
  const shouldFadeAndTruncate = doFade && !truncated;
  const containerStyles = shouldFadeAndTruncate
    ? classNames(descriptionContainerStyle, styles.truncated)
    : classNames(descriptionContainerStyle);
  const actualHeight = shouldFadeAndTruncate ? contentHeight : "100%";

  const toggle = () => {
    setTruncated(!truncated);
  };

  if (productType === "RetiredStaticBundle") return null;

  return (
    <Accordion
      id="accordion-description"
      data-testid="accordion:description"
      label="Product description"
      animated={true}
      open={open}
      instanceName={PRODUCT_DESCRIPTION_ACCORDION_INSTANCE_NAME}
      responsive
    >
      <div
        className={containerStyles}
        ref={descriptionRef}
        style={{ maxHeight: actualHeight }}
      >
        <p data-testid="description:code">
          <strong>Product code: {displayCode}</strong>
        </p>

        <DescriptionIcons icons={icons} />

        <div
          className={styles.descriptionContent}
          data-testid="description:content"
          dangerouslySetInnerHTML={{
            __html: description,
          }}
        />
        {endorsement && (
          <div
            id="description:endorsement"
            data-testid="description:endorsement"
            className={styles.endorsement}
          >
            <h3
              className={styles.endorsementTitle}
              data-testid="description:endorsement:title"
            >
              {endorsement.title}
            </h3>
            {endorsement.description && (
              <div
                data-testid="description:endorsement:description"
                id="description:endorsement:description"
                dangerouslySetInnerHTML={{ __html: endorsement.description }}
              />
            )}
          </div>
        )}

        {!hideDescriptionLinks && <DescriptionLinks links={links} />}

        {showBeautySocietyLink && (
          <div
            className={styles.beautySocityLink}
            data-testid="beauty-society:link"
          >
            <UnderlinedButton
              tag="a"
              target="_self"
              href={"https://www.johnlewis.com/our-services/beauty-society"}
            >
              Read more about beauty consultations
            </UnderlinedButton>
          </div>
        )}
      </div>
      {doFade && (
        <TertiaryButton
          onClick={toggle}
          IconComponent={
            truncated ? Minus24PxOutlinedIcon : Plus24PxOutlinedIcon
          }
          iconPositionRight
          aria-expanded={!truncated ? false : true}
          aria-controls="product-description-toggle"
          data-testid="product:description:showmoreless"
          small={true}
        >
          <span>{!truncated ? "Show more" : "Show less"}</span>
        </TertiaryButton>
      )}
    </Accordion>
  );
}
